import { CalendarEvent, EventStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/calendar_event.pb';
import { Source } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/source_tenant.pb';
import { ListMappingsResponse } from '@weave/schema-gen-ts/dist/schemas/syncapp/mappings/v1/service.pb';
import {
  APPOINTMENT_STATUS_MAPPING_V3,
  AppointmentStatusMapping,
  AppointmentStatusType,
  AppointmentStatusV3Enum,
} from './types';

type GetAppointmentStatusListArgs = {
  appointmentStatusData?: ListMappingsResponse;
  appointmentDetails?: CalendarEvent;
  sourceTenants?: Source[];
  isIntegrated?: boolean;
};

type GetIntegratedAppointmentStatusListArgs = Omit<GetAppointmentStatusListArgs, 'isIntegrated'>;

export const getIntegratedAppointmentStatusList = ({
  appointmentStatusData,
  appointmentDetails,
  sourceTenants,
}: GetIntegratedAppointmentStatusListArgs) => {
  if (!appointmentStatusData || !appointmentDetails || !sourceTenants) return [];

  const getSourceAppointmentStatusMapping = () => {
    const sourceTenantList = flattenSourceTenants(sourceTenants);
    const appointmentSourceTenantDetails = sourceTenantList.find(
      ({ sourceTenantId, parentSourceTenantId }) =>
        sourceTenantId === appointmentDetails.sourceTenantId ||
        parentSourceTenantId === appointmentDetails.sourceTenantId
    );
    const appointmentSourceDetails = appointmentStatusData?.mappings?.find(
      (mapping) =>
        appointmentSourceTenantDetails?.sourceId && appointmentSourceTenantDetails.sourceId === mapping.sourceId
    );

    let mappings = appointmentSourceDetails?.mappings
      .filter((mapping) => mapping.value.length)
      .map((mapping) => mapping);

    if (mappings?.length === 0 && appointmentSourceDetails?.externalValues) {
      mappings = appointmentSourceDetails?.externalValues.map((status) => {
        return {
          key: status.label,
          value: [{ label: status.label, value: status.value }],
        };
      });
    }

    return mappings;
  };

  const sourceAppointmentStatusMapping = getSourceAppointmentStatusMapping();

  const appointmentStatuses = sourceAppointmentStatusMapping?.reduce((acc: AppointmentStatusType[], curr) => {
    const statusKey = curr.key as keyof typeof AppointmentStatusMapping;
    const statusValue = AppointmentStatusMapping[statusKey];
    const statusMap = APPOINTMENT_STATUS_MAPPING_V3.get(statusValue as unknown as AppointmentStatusV3Enum);

    curr.value.forEach((assignedStatus) => {
      // Skip unknown status
      if ((statusValue as unknown as AppointmentStatusV3Enum) === AppointmentStatusV3Enum.UNKNOWN_EVENT_STATUS) {
        return;
      }

      const statusObject: AppointmentStatusType = {
        key: statusValue as unknown as EventStatus,
        value: assignedStatus.value,
        label: assignedStatus.label,
        icon: statusMap?.iconName ?? 'pending-small',
        variant: statusMap?.variant,
      };

      acc.push(statusObject);
    });

    return acc;
  }, []);

  return appointmentStatuses || [];
};

export type SourceTenantListItem = {
  sourceTenantId?: string;
  parentSourceTenantId?: string;
  sourceId?: string;
  name?: string;
  pmsName?: string;
};

// TODO: Move to a common API schema
export const flattenSourceTenants = (sourceTenants: Source[] = []) => {
  return sourceTenants.reduce<SourceTenantListItem[]>((list, source) => {
    const sites = source.sites?.length
      ? source.sites.map((site) => ({
          parentSourceTenantId: source.sourceTenantId,
          sourceTenantId: site.sourceTenantId,
          sourceId: source.id,
          name: site.name,
          pmsName: source.pmsName,
        }))
      : [
          {
            parentSourceTenantId: source.sourceTenantId,
            sourceTenantId: source.sourceTenantId,
            sourceId: source.id,
            name: source.sourceName,
            pmsName: source.pmsName,
          },
        ];

    return list.concat(sites);
  }, []);
};
